import ButtonCommon from 'components/common/ButtonCommon';
import BodySectionHeader from 'components/fx/components/BodySectionHeader';
import {
  DESKTOP_MIN_WIDTH,
  FX_TRANSACTION_ADD_PREFIX,
  SCREEN_ID_EXCHANGE_STEP_1,
  SCREEN_ID_EXCHANGE_STEP_2,
} from 'constants';
import {
  FX_ADD_TRANSACTION_PICKUP_LOCATION,
  PICKUP_LOCATION_TYPE_1,
  PICKUP_LOCATION_TYPE_2,
  RESET_FX_ADD_TRANSACTION_PICKUP_LOCATION,
} from 'constants/fx';
import useClarity from 'hooks/clarity/useClarity';
import useFxAddTransaction from 'hooks/fx/useFxAddTransaction';
import useFxFormAddTransactionStore from 'hooks/fx/useFxFormAddTransactionStore';
import useFxPickupDateNoticePopup from 'hooks/fx/useFxPickupDateNoticePopup';
import useAutoShowEventModal from 'hooks/useAutoShowEventModal';
import useCommonStore from 'hooks/useCommonStore';
import { useDetectDevice } from 'hooks/useDetectDevice';
import useLang from 'hooks/useLang';
import useScrollIntoViewDelay from 'hooks/useScrollIntoViewDelay';
import useToastify from 'hooks/useToastify';
import { isEmpty } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import 'react-toastify/dist/ReactToastify.css';
import styled from 'styled-components';
import { domUtils } from 'utils';
import CalculatorForm from '../CalculatorForm';
import PickupLocationDetails from '../PickupLocationDetails';
import PickupLocationTypes from '../PickupLocationTypes';

const initialFormField = RESET_FX_ADD_TRANSACTION_PICKUP_LOCATION;

const SCREEN_ID = SCREEN_ID_EXCHANGE_STEP_2;

const PickupLocation = ({ handleOnToggleCancelPopup }) => {
  const { t } = useLang();
  const { isDesktop } = useDetectDevice();

  const [information, setInformation] = useState(initialFormField);
  const [branches, setBranches] = useState(null);
  const [
    isTriggerOpenSelectPickupLocationDrawer,
    setTriggerOpenSelectPickupLocationDrawer,
  ] = useState(false);

  const {
    getTitleBodySection,
    setPickupLocationBranches,
    isFxAsRegister,
    isFxAsGuest,
  } = useFxAddTransaction();
  const {
    pickupLocation,
    addTransactionCurrentStep: currentStep,
    setAddTransactionCurrentStep,
    pickupLocationType,
    pickupLocationSelected,
    setPickupLocation,
    confirmInformationEmail,
  } = useFxFormAddTransactionStore();
  const { currentUser } = useCommonStore();

  const { triggerToaster } = useToastify({
    content: t('login_success_label'),
  });

  const { isFxPickupDateNoticePopupExpired } = useFxPickupDateNoticePopup();

  const isNextDisabled = isEmpty(pickupLocationSelected);

  // scroll to calculator form
  const validRef = useRef(null);
  useScrollIntoViewDelay(validRef, validRef, []);

  useEffect(() => {
    if (isEmpty(branches)) {
      setPickupLocationBranches(setBranches);
    }
  }, [branches]);

  useEffect(() => {
    if (isEmpty(information[FX_ADD_TRANSACTION_PICKUP_LOCATION])) {
      setInformation(pickupLocation);
    }
  }, [pickupLocation]);

  useEffect(() => {
    if (isTriggerOpenSelectPickupLocationDrawer) {
      if (pickupLocationType === PICKUP_LOCATION_TYPE_2) {
        const navbarToggleEle = document.getElementsByClassName(
          `${PICKUP_LOCATION_TYPE_2}-navbar-toggle`,
        )[0];
        if (navbarToggleEle) {
          navbarToggleEle.click();
        }
        if (isFxPickupDateNoticePopupExpired) {
          setTriggerOpenSelectPickupLocationDrawer(false);
        }
      } else if (pickupLocationType === PICKUP_LOCATION_TYPE_1) {
        const navbarToggleEle = document.getElementsByClassName(
          `${PICKUP_LOCATION_TYPE_1}-navbar-toggle`,
        )[0];
        if (navbarToggleEle) {
          navbarToggleEle.click();
        }
        setTriggerOpenSelectPickupLocationDrawer(false);
      }
    }
  }, [pickupLocationType, isTriggerOpenSelectPickupLocationDrawer]);

  useEffect(() => {
    if (!isEmpty(pickupLocationType)) return;
    if (isFxAsRegister) {
      triggerToaster();
      domUtils.replaceUrlWithoutReload({ path: FX_TRANSACTION_ADD_PREFIX });
    }
  }, [isFxAsRegister, pickupLocationType]);

  const getTitleSection = () => {
    if (isEmpty(pickupLocationSelected))
      return t('fx_add_transaction_pickup_location');
    return t('fx_add_transaction_confirm_pickup_location');
  };

  const handleBackStep = () => {
    setAddTransactionCurrentStep(currentStep - 1);

    const isCachePickupLocation =
      pickupLocationType !== '' &&
      !isEmpty(information[FX_ADD_TRANSACTION_PICKUP_LOCATION]);
    if (isCachePickupLocation) {
      setPickupLocation(information);
    }
  };
  const handleNextStep = () => {
    const isAllowSubmit = true;
    if (isAllowSubmit) {
      setAddTransactionCurrentStep(currentStep + 1);
    }
  };
  const renderCalculatorFormMobile = () => {
    if (isDesktop) return null;
    return <CalculatorForm ref={validRef} isCalculatorFormAccordion={true} />;
  };
  const renderActionsButton = () => {
    return (
      <NextWrap>
        {renderCalculatorFormMobile()}
        <ActionWrapper>
          <ButtonCommonWrap
            style={{
              width: isEmpty(pickupLocationSelected) && '100%',
            }}
          >
            <ButtonCommon
              value={t('button_back')}
              onClick={handleBackStep}
              styles={{
                margin: '0px',
                width: !isDesktop && '100%',
              }}
              color="var(--ds-c-blue)"
              background="var(--ds-c-white)"
              borderNameVariable={isDesktop && '--border-filter'}
              isFill={false}
            />
          </ButtonCommonWrap>
          <ButtonCommon
            value={t('button_confirm')}
            color="var(--c-primary)"
            background="var(--bg-primary)"
            isFill={true}
            styles={{
              margin: '0px',
              width: isDesktop ? '150px' : '50%',
              ...(isEmpty(pickupLocationSelected) && {
                opacity: 0,
                cursor: 'default',
                display: !isDesktop && 'none',
              }),
            }}
            onClick={handleNextStep}
            isDisabled={isNextDisabled}
            isUseKeyDown={!isNextDisabled}
          />
        </ActionWrapper>
      </NextWrap>
    );
  };

  const renderPickupLocationBodyContent = () => {
    if (isEmpty(pickupLocationSelected))
      return (
        <PickupLocationTypes
          branches={isEmpty(branches) ? [] : branches}
          information={information}
          setInformation={setInformation}
          isTriggerOpenSelectPickupLocationDrawer={
            isTriggerOpenSelectPickupLocationDrawer
          }
          setTriggerOpenSelectPickupLocationDrawer={
            setTriggerOpenSelectPickupLocationDrawer
          }
        />
      );
    return (
      <PickupLocationDetails
        branches={isEmpty(branches) ? [] : branches}
        information={information}
        setInformation={setInformation}
        setTriggerOpenSelectPickupLocationDrawer={
          setTriggerOpenSelectPickupLocationDrawer
        }
      />
    );
  };

  useAutoShowEventModal({
    screenId: SCREEN_ID,
    params: [],
    modalStyles: {},
    modalOptions: {},
  });

  const { clarityAPI } = useClarity();
  useEffect(() => {
    if (isFxAsGuest) {
      const customUserIdIdentify = confirmInformationEmail;
      if (!customUserIdIdentify) return;
      clarityAPI.identify(customUserIdIdentify, '', SCREEN_ID_EXCHANGE_STEP_1);
      return;
    }
    const customUserIdIdentify = currentUser?.senderId;
    if (!customUserIdIdentify) return;
    clarityAPI.identify(customUserIdIdentify, '', SCREEN_ID_EXCHANGE_STEP_1);
  }, [isFxAsGuest, currentUser, confirmInformationEmail]);

  return (
    <>
      <BodySectionHeader
        titleBodySection={getTitleBodySection()}
        titleSection={getTitleSection()}
        CalculatorComponent={isDesktop ? <CalculatorForm /> : null}
        onToggleCancelPopup={handleOnToggleCancelPopup}
      />
      <PickupLocationStyled>
        {renderPickupLocationBodyContent()}
        {renderActionsButton()}
      </PickupLocationStyled>
    </>
  );
};

const PickupLocationStyled = styled.div`
  display: inline-block;
  width: 100%;
  margin-top: -8px;
  padding-inline: 0px;
  padding-bottom: 140px;
  background: var(--ds-c-white);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 437px;
    margin-top: 0px;
    padding-inline: 0px;
    padding-bottom: unset;
  }
`;

export const NextWrap = styled.div`
  position: fixed;
  bottom: 0;
  z-index: 1;
  width: 100%;
  height: fit-content;
  margin-inline: -16px;
  padding-inline: 0px;
  background: var(--ds-c-white);

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    position: unset;
    margin-top: 40px;
    margin-bottom: 24px;
    margin-inline: unset;
    padding-inline: unset;
    width: 437px;
    height: fit-content;
    border-top: unset;
  }
`;
export const ActionWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 77px;
  width: 100%;
  background: var(--ds-c-white);
  border-top: 1px solid var(--ds-bg-2);
  padding-inline: 16px;
  margin: 0px;

  @media screen and (min-width: ${DESKTOP_MIN_WIDTH}px) {
    width: 100%;
    height: 44px;
    border-top: unset;
    padding-inline: unset;
    margin: 0px;
  }
`;
export const ButtonCommonWrap = styled.div`
  width: 50%;
`;

export default PickupLocation;
